import { AccessChoiceHeading, AccessChoiceList } from "@/components/connectors/AccessChoice.tsx";
import { accessChoiceFootnote } from "@/shared/accessSelection.ts";
import { useAccessChoice } from "@/shared/useAccessChoice.ts";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { pageColumnClasses, screenGutter } from "@/components/layout/contentLayout.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import {
  type AuthFlow,
  isAuthFlow,
  providerName,
  providerStartPath,
} from "@/views/authProviders.ts";
import { ACCESS_VOCABULARY_PARAM, ACCESS_VOCABULARY_VERSION } from "@/types/auth.ts";
import { NATIVE_CALLBACK_PARAM } from "@/types/nativeSignIn.ts";
import { useMemo } from "react";
import { useSearchParams } from "wouter";

/**
 * The flow this tab was parked from, or `null` when the URL names one this
 * build does not know.
 *
 * Absent and unrecognised are deliberately different answers. Absent means a
 * client that predates the parameter, and `login` is what those meant.
 */
function resolveFlow(flowParam: string | null): AuthFlow | null {
  if (flowParam === null) return "login";
  return isAuthFlow(flowParam) ? flowParam : null;
}

/**
 * Stands between "Continue with Google" and Google's consent screen, so the
 * decision about what okthx may read is made in okthx's words. Google's screen
 * then asks for exactly what was ticked here — a user who wants none of it sees
 * a sign-in prompt rather than a wall of permissions they have to refuse
 * one-by-one.
 *
 * The api parks the OAuth tab here (`/base` redirects when the unconnected-accounts
 * gate is on and no `access` was supplied), exactly as it parks it on
 * `GrantRequired`. Continuing navigates this same tab back to the provider start
 * route with the chosen ids, which is why nothing here listens for the OAuth
 * broadcast: the tab that opened this one is still waiting on it.
 *
 * Everything starts ticked. The list is what okthx is for, and a blank form
 * would read as a prompt to justify each item rather than a chance to hold one
 * back.
 */
export const ScopeSelection = () => {
  const [params] = useSearchParams();
  const provider = params.get("provider") ?? "google";
  const app = params.get("app");
  const returnTo = params.get("returnTo");
  // Which start route Continue goes back to, and the one place this page can
  // get something badly wrong. `login` signs the user in AS the account they
  // pick; `add_account` adds it under the user they already are. So an
  // unrecognised value must not quietly become either: a tab parked from
  // add-account that fell back to `login` would sign the user into the account
  // they meant to add and replace the session in the tab waiting behind it.
  //
  // Absent is different from unrecognised. Absent means a client that predates
  // the parameter, and `login` is what those meant.
  const flow = resolveFlow(params.get("flow"));
  const codeChallenge = params.get("code_challenge");
  const nativeCallback = params.get(NATIVE_CALLBACK_PARAM);

  // What this provider can offer, and everything ticked to begin with. An
  // unknown provider falls back to Google's list, matching the start-path
  // fallback below, so the page never renders an empty form.
  const { offered, selected, toggle } = useAccessChoice(provider);

  const name = providerName(provider);

  const continueUrl = useMemo(() => {
    if (flow === null) return null;
    const base = resolveApiPath(providerStartPath(provider, flow));
    const query = new URLSearchParams();
    // Always set, even when empty: the api treats an absent `access` as "this
    // request never came from here" and would bounce back to this page.
    query.set("access", selected.join(","));
    // Which meaning these ids carry. Sent beside `access` and never without it:
    // an api that reads an unmarked request falls back to the older, narrower
    // vocabulary, which is the safe answer for a client it cannot identify.
    query.set(ACCESS_VOCABULARY_PARAM, ACCESS_VOCABULARY_VERSION);
    if (app) query.set("app", app);
    if (returnTo) query.set("returnTo", returnTo);
    // A native sign-in has to continue with the challenge it began with: the app
    // still holds the matching verifier and will finalize against it. Dropping
    // it here would turn the second round away as an app too old to sign in.
    if (codeChallenge) query.set("code_challenge", codeChallenge);
    // And end where the app is waiting for it.
    if (nativeCallback) query.set(NATIVE_CALLBACK_PARAM, nativeCallback);
    return `${base}?${query.toString()}`;
  }, [provider, flow, app, returnTo, selected, codeChallenge, nativeCallback]);

  // A flow this build does not know. Rather than guess — and the wrong guess
  // signs the user into an account they meant to add — say so and let them
  // restart from a surface that supplies a flow we recognise.
  if (continueUrl === null) {
    return (
      <div
        className={cn(
          "flex flex-col grow-1 items-center justify-center min-h-dvh",
          screenGutter,
          pageColumnClasses["8"],
        )}
      >
        <div className="flex flex-col items-center gap-4 w-full max-w-[520px] py-10 text-center">
          <OkThxWordmark className="w-[136px] h-[31.98px]" />
          <h1 className="text-2xl font-semibold">This link is out of date</h1>
          <p className="text-muted-foreground leading-6">
            Close this tab and start again from Okthx.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col grow-1 items-center justify-center min-h-dvh",
        screenGutter,
        pageColumnClasses["8"],
      )}
    >
      <div className="flex flex-col items-center gap-8 w-full max-w-[520px] py-10">
        <div className="flex flex-col items-center gap-4 text-center">
          <OkThxWordmark className="w-[136px] h-[31.98px]" />
          <AccessChoiceHeading />
        </div>

        <AccessChoiceList offered={offered} selected={selected} onToggle={toggle} />

        <div className="flex flex-col items-center gap-2">
          <Button
            asChild
            variant="popover"
            className="border-default h-12 px-8"
            data-statsig-name="auth-scope-selection-continue"
          >
            <a href={continueUrl}>Continue with {name}</a>
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            {accessChoiceFootnote(name, selected.length)}
          </p>
        </div>
      </div>
    </div>
  );
};
